import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";

const TIERS = [
  {
    name: "Free Generator",
    price: "$0",
    period: "forever",
    desc: "The AI scene generator stays free for any classroom. Start with 2D scenes and full lesson docs.",
    features: ["5 VR lesson builds / month", "Outlines, scripts & assessments", "Standards auto-tagging", "2D storyboard preview"],
    cta: "Start free",
    to: "/register",
    lookupKey: null,
    highlight: false,
  },
  {
    name: "Family",
    price: "$19.99",
    period: "/mo",
    desc: "Household-friendly billing for families learning at home.",
    features: ["10 VR lesson builds / month", "1 family seat", "Full VR lesson library access", "VR export & headset mode", "Household billing"],
    cta: "Choose Family",
    lookupKey: "vividvr_family_monthly",
    highlight: false,
  },
  {
    name: "Classroom",
    price: "$99",
    period: "/mo",
    desc: "One educator, one classroom license for up to 30 students.",
    features: ["25 VR lesson builds / month", "1 educator seat", "Classroom license — 30 students", "VR export & rostering", "Standard member support"],
    cta: "Choose Classroom",
    lookupKey: "vividvr_classroom_monthly",
    highlight: true,
  },
  {
    name: "Founding",
    price: "$199",
    period: "/mo",
    desc: "Everything in Classroom, plus early access and the founding community — pricing locked in.",
    features: ["Unlimited VR lesson builds", "Classroom license — 30 students", "Early-access features", "Founding community access", "Locked-in founding pricing", "Priority support"],
    cta: "Join Founding",
    lookupKey: "vividvr_founding_monthly",
    highlight: false,
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);

  const startCheckout = async (tier) => {
    if (!tier.lookupKey) { nav(tier.to); return; }
    if (!localStorage.getItem("token")) {
      toast.info("Create your free account first, then subscribe.");
      nav("/register");
      return;
    }
    setLoadingKey(tier.lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoadingKey(null);
    }
  };
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-3xl mx-auto animate-in-up">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Transparent pricing for every classroom</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            Start free. Upgrade when your classroom is ready.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            No sales call, no per-seat quote. Every plan is published, the prices are public, and you can switch or cancel
            anytime. Try VividVR for free, then move up when you need full VR export and rostering.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12 items-stretch">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={`pricing-tier-${t.name.toLowerCase().split(" ")[0]}`}
              className={`relative rounded-3xl border p-6 flex flex-col ${
                t.highlight ? "border-primary shadow-2xl shadow-primary/15 bg-card ring-2 ring-primary/20" : "border-border bg-card"
              }`}
            >
              {t.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground border-0">Most popular</Badge>
              )}
              <h3 className="font-display font-bold text-lg">{t.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="text-4xl font-display font-extrabold tracking-tight">{t.price}</span>
                <span className="text-muted-foreground mb-1 text-sm">{t.period}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{t.desc}</p>
              <ul className="mt-5 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startCheckout(t)}
                disabled={!!t.lookupKey && loadingKey === t.lookupKey}
                data-testid={`pricing-cta-${t.name.toLowerCase().split(" ")[0]}`}
                className={`mt-6 w-full rounded-full ${t.highlight ? "" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}
              >
                {t.lookupKey && loadingKey === t.lookupKey ? <Loader2 className="w-4 h-4 animate-spin" /> : t.cta}
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Annual billing available on request — every plan ships with a 14-day money-back guarantee. Prices in USD, billed
          transparently through Stripe.
        </p>

        <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 mt-12 text-center">
          <Sparkles className="w-8 h-8 text-primary mx-auto" />
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight mt-3">Need a district or non-profit agreement?</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Email <a href="mailto:hello@vividvrlessons.com" className="text-primary font-semibold">hello@vividvrlessons.com</a> and we'll get back within a business day.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="pricing-try-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-7">
              Try the free generator <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => nav("/standards")} className="rounded-full h-12 px-7">
              See standards coverage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
